import { plateFont } from '../../utils/fonts';
import * as React from 'react';
import { Typography, IconButton, Tooltip, Box } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HOUR_HEIGHT } from './calendarConstants';
import { getHour, getMinutes, getDurationHours, parseISO } from './hooks/dateUtils';
import useCalendarColors from './hooks/useCalendarColors';
import useCalendarState from './hooks/useCalendarState';
import useHighlightScroll from './hooks/useHighlightScroll';
import DraggableEvent from './DraggableEvent';
import { MaintenanceTooltipContent } from './EventTooltipContent';

const PARTS_ORDER_CONFIG = {
  not_ordered: { color: '#ef4444', bg: '#fee2e2' },
  ordered: { color: '#d97706', bg: '#fef3c7' },
  arrived: { color: '#059669', bg: '#d1fae5' },
  not_needed: { color: '#64748b', bg: '#f1f5f9' },
};

/**
 * AllViewEvent - Event card for the "All" calendar view
 * Shows vehicle, employee names, and service bay info inline
 * Positioned absolutely within day column based on overlap calculation
 */
function AllViewEvent({
  event,
  startHour: workStartHour = 8,
  endHour: workEndHour = 16,
  dayIndex, // Required for multi-day event drag handling
  overlapIndex = 0,
  overlapTotal = 1,
  employees = [],
  serviceBays = [],
}) {
  const { openEditEventDialog, highlightedMaintenanceId, highlightedScheduleId, events } = useCalendarState();
  const { resolveEvent } = useCalendarColors();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // A work order can have several slots; a calendar search highlights just the one it
  // navigated to, while the ?highlight= URL param still highlights them all.
  const isHighlighted = highlightedScheduleId
    ? highlightedScheduleId === event.id
    : highlightedMaintenanceId === event.maintenance_id;

  useHighlightScroll(event.id, isHighlighted);

  // Guard against invalid event data
  if (!event.start_time || !event.end_time) {
    return null;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    openEditEventDialog(event);
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    navigate(`/maintenances/${event.maintenance_id}`);
  };

  const startTime = parseISO(event.start_time);
  const endTime = parseISO(event.end_time);
  const rawStartHour = getHour(startTime) + getMinutes(startTime) / 60;
  const rawDurationHours = getDurationHours(event.start_time, event.end_time);
  const rawEndHour = rawStartHour + rawDurationHours;

  // For multi-day events, use pre-calculated clamped hours; otherwise clamp to work schedule
  let clampedStartHour, clampedEndHour;
  if (event._clampedStartHour !== undefined && event._clampedEndHour !== undefined) {
    // Multi-day event with pre-calculated clamped hours for this day
    clampedStartHour = event._clampedStartHour;
    clampedEndHour = event._clampedEndHour;
  } else {
    // Single-day event: clamp to work schedule bounds
    clampedStartHour = Math.max(workStartHour, rawStartHour);
    clampedEndHour = Math.min(workEndHour, rawEndHour);
  }

  const clampedDuration = clampedEndHour - clampedStartHour;

  // Skip rendering if event is entirely outside work hours
  if (clampedDuration <= 0) {
    return null;
  }

  const top = (clampedStartHour - workStartHour) * HOUR_HEIGHT + 2;
  const height = clampedDuration * HOUR_HEIGHT - 4;
  const durationHours = clampedDuration; // Use clamped duration for display decisions

  const colors = resolveEvent(event, isHighlighted);
  const config = colors.stateConfig;

  // Get employee names
  const employeeNames = (event.employee_ids || [])
    .map((id) => {
      const emp = employees.find((e) => e.id === id);
      return emp?.name || emp?.initials || '';
    })
    .filter(Boolean)
    .join(', ');

  // Get service bay name
  const serviceBayName = event.service_bay_id
    ? serviceBays.find((b) => b.id === event.service_bay_id)?.name || ''
    : '';

  // Format time from Date object (HH:mm with colon separator)
  const formatTimeFromDate = (date) => {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Format short date (e.g., "7.1.")
  const formatShortDate = (date) => {
    return `${date.getDate()}.${date.getMonth() + 1}.`;
  };

  // Check if event spans multiple days
  const isMultiDay = event._isMultiDay || (startTime.toDateString() !== endTime.toDateString());

  // Build the time display string
  const getTimeDisplayString = () => {
    if (isMultiDay) {
      // Multi-day: show "7.1. 08:00 - 8.1. 10:00"
      return `${formatShortDate(startTime)} ${formatTimeFromDate(startTime)} - ${formatShortDate(endTime)} ${formatTimeFromDate(endTime)}`;
    } else {
      // Single-day: show "08:00 - 10:00"
      return `${formatTimeFromDate(startTime)} - ${formatTimeFromDate(endTime)}`;
    }
  };

  const otherSchedules = events.filter(
    (e) => e.maintenance_id === event.maintenance_id && e.id !== event.id
  );

  const tooltipContent = (
    <MaintenanceTooltipContent event={event} config={config} timeString={getTimeDisplayString()} employeeNames={employeeNames} serviceBayName={serviceBayName} otherSchedules={otherSchedules} tagName={colors.tagName} tagColor={colors.tagColor} />
  );

  return (
    <DraggableEvent
      event={event}
      eventType="maintenance"
      top={top}
      height={height}
      backgroundColor={colors.bg}
      borderColor={colors.border}
      ringColor={colors.ringColor}
      colorTagId={event.color_tag_id}
      colorTagMode={colors.tagMode}
      highlighted={isHighlighted}
      onClick={handleClick}
      dayIndex={dayIndex}
      gridStartHour={workStartHour}
      gridEndHour={workEndHour}
      overlapIndex={overlapIndex}
      overlapTotal={overlapTotal}
      tooltipContent={tooltipContent}
      sx={{
        animation: isHighlighted ? 'pulse 1s ease-in-out 3' : undefined,
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.02)' },
        },
      }}
    >
      {/* View Details button - show for events 1h or taller */}
      {durationHours >= 1 && (
        <Tooltip title={t('View maintenance details')}>
          <IconButton
            size="small"
            onClick={handleViewDetails}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              p: 0.25,
              color: colors.text,
              bgcolor: 'rgba(255, 255, 255, 0.6)',
              pointerEvents: 'auto',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Vehicle Registration */}
      <Typography
        sx={{
          ...plateFont,
          fontWeight: 700,
          fontSize: '11px',
          color: colors.text,
          lineHeight: 1.2,
          pr: durationHours >= 1 ? 2.5 : 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.vehicle_registration}
      </Typography>

      {/* Colour tag name */}
      {colors.tagName && durationHours >= 1 && (
        <Box
          component="span"
          data-testid="calendar-event-color-tag-name"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            mt: '1px',
            px: '5px',
            py: '1px',
            borderRadius: '10px',
            maxWidth: '100%',
            fontSize: '9px',
            fontWeight: 600,
            color: colors.text,
            backgroundColor: colors.text === '#ffffff' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.45)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          <Box
            component="span"
            sx={{ width: 5, height: 5, borderRadius: '2px', backgroundColor: colors.tagColor, flexShrink: 0 }}
          />
          {colors.tagName}
        </Box>
      )}

      {/* Vehicle Model */}
      {durationHours >= 0.75 && (event.vehicle_manufacturer || event.vehicle_model) && (
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 500,
            color: colors.text,
            opacity: 0.85,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            mt: 0.5,
            mb: 0.5,
          }}
        >
          {[event.vehicle_manufacturer, event.vehicle_model].filter(Boolean).join(' ')}
        </Typography>
      )}

      {/* Time - show actual booking time range */}
      <Typography
        sx={{
          fontSize: '9px',
          color: colors.text,
          lineHeight: 1.2,
        }}
      >
        {getTimeDisplayString()}
      </Typography>

      {/* Customer name (show if there's space and customer is assigned) */}
      {durationHours >= 0.75 && event.customer_name && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
          <PersonIcon sx={{ fontSize: 10, color: colors.text, opacity: 0.8 }} />
          <Typography
            sx={{
              fontSize: '9px',
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {event.customer_name}
          </Typography>
        </Box>
      )}

      {/* Employee names (show if there's space and no customer shown) */}
      {durationHours >= 0.75 && !event.customer_name && employeeNames && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
          <PersonIcon sx={{ fontSize: 10, color: colors.text, opacity: 0.8 }} />
          <Typography
            sx={{
              fontSize: '9px',
              color: colors.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
            }}
          >
            {employeeNames}
          </Typography>
        </Box>
      )}

      {/* Parts order status badge */}
      {event.parts_order_status && PARTS_ORDER_CONFIG[event.parts_order_status] && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            mt: 0.25,
            padding: '1px 5px',
            borderRadius: '10px',
            fontSize: '8px',
            fontWeight: 500,
            backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].bg,
            color: PARTS_ORDER_CONFIG[event.parts_order_status].color,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: PARTS_ORDER_CONFIG[event.parts_order_status].color }} />
          {t(`parts_order.${event.parts_order_status}_short`)}
        </Box>
      )}

      {/* Service bay (show if there's space and assigned) */}
      {durationHours >= 1 && serviceBayName && (
        <Typography
          sx={{
            fontSize: '9px',
            color: colors.text,
            opacity: 0.85,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          📍 {serviceBayName}
        </Typography>
      )}

      {/* Description (show if there's more space) */}
      {durationHours >= 1.5 && event.description && (
        <Typography
          sx={{
            fontSize: '9px',
            color: colors.text,
            opacity: 0.85,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {event.description}
        </Typography>
      )}
    </DraggableEvent>
  );
}

export default AllViewEvent;
